import config from '@/config';
import Mp3Recorder from '@/features/chats/sub-features/audio-recorder/utils/Mp3Recorder';
import {
   deleteSession,
   getElapsedMs,
   getElapsedSec,
   getSession,
   isActive,
   setSession,
   updateSession,
} from '@/features/chats/sub-features/audio-recorder/utils/recorderSessions';
import { blockOfflineAction } from '@/shared/utils/network';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AudioLimitWorker from '../utils/audioLimitWorker?worker'; // Vite worker import

const MIC_ERRORS = {
   NotAllowedError: 'Microphone permission denied',
   NotFoundError: 'No microphone found',
};

const LIMIT_CHECK_INTERVAL_MS = 250;

/**
 * Per-chat audio recorder hook with zero-interval design.
 */
export function useAudioRecorder({ activeChatJid, onStop } = {}) {
   const audioLimit = config.audioLimitInSec;

   const isCallActive = useSelector(
      state => state.callkitData?.callScreenStatus && state.callkitData.callScreenStatus !== 'idle',
   );

   const busy = useRef(false);
   const canSendRef = useRef(false);
   const limitRef = useRef(false);

   const existing = getSession(activeChatJid);
   const initSec = getElapsedSec(existing);

   const [status, setStatus] = useState(() => existing?.status || 'idle');
   const [error, setError] = useState(null);
   const [stream, setStream] = useState(() => existing?.stream || null);
   const [canSend, setCanSend] = useState(() => initSec >= 1);
   const [limitReached, setLimitReached] = useState(() => initSec >= audioLimit);

   canSendRef.current = canSend;
   limitRef.current = limitReached;

   useEffect(() => {
      Mp3Recorder.prewarm();
   }, []);

   useEffect(() => {
      return () => {
         const session = getSession(activeChatJid);
         if (session && session.status === 'recording') {
            session.accumulatedMs += Date.now() - session.startedAt;
            session.recorder?.stopTracks();
            session.recorder?.pause();
            updateSession(activeChatJid, { status: 'paused', stream: null });
         }
      };
   }, [activeChatJid]);

   // Returns the elapsed time in RAW MILLISECONDS — not pre-floored seconds. The caller
   // (AudioRecorder.jsx) formats this with the same millisToMinutesAndSeconds() used for
   // the final sent message's duration label, so the live timer and the persisted
   // duration can never disagree. They used to: this used to return Math.floor(ms/1000),
   // and the live UI floored that again for display, while the sent message's duration
   // (read from the actual encoded audio afterwards) got rounded — so a recording stopped
   // at, say, 60.6s real time displayed "1:00" live (floored twice) but "1:01" once sent
   // (rounded once), even though both numbers describe the exact same recording.
   const tick = useCallback(() => {
      const session = getSession(activeChatJid);
      if (!session || session.status !== 'recording') {
         return getElapsedMs(session);
      }

      const sec = getElapsedSec(session);

      if (!canSendRef.current && sec >= 1) {
         canSendRef.current = true;
         setCanSend(true);
      }

      if (!limitRef.current && sec >= audioLimit) {
         limitRef.current = true;
         setLimitReached(true);
         session.accumulatedMs += Date.now() - session.startedAt;
         session.recorder?.stopTracks();
         session.recorder?.pause();
         updateSession(activeChatJid, { status: 'paused', stream: null });
         setStream(null);
         setStatus('paused');
         return getElapsedMs(session); // re-read the now-frozen final value
      }

      return getElapsedMs(session);
   }, [activeChatJid, audioLimit]);

   // Enforces the recording limit independently of the UI's requestAnimationFrame loop.
   // rAF is throttled/paused entirely by the browser while the tab is hidden, so relying
   // on it alone to call tick() let recordings run well past audioLimit whenever the user
   // switched tabs mid-recording. A main-thread setInterval is better but still gets
   // clamped to at best 1/sec in hidden tabs (and throttled much further — to as
   // infrequently as once a minute — the longer a tab stays hidden), so the recording
   // could still overshoot the limit by a second or more before being caught. A dedicated
   // Worker's timers aren't subject to that visibility-based throttling at all, so ticking
   // from there keeps enforcement close to on-time regardless of how long the tab has
   // been backgrounded.
   useEffect(() => {
      if (status !== 'recording') return undefined;

      let intervalId = null;
      let worker = null;

      try {
         worker = new AudioLimitWorker();
         worker.onmessage = ({ data }) => {
            if (data.type === 'TICK') tick();
         };
         worker.postMessage({ type: 'START', intervalMs: LIMIT_CHECK_INTERVAL_MS });
      } catch {
         // Worker unavailable (SSR, old browser) — fall back to a main-thread interval.
         // Still far better than relying on rAF alone, even with the throttling caveat.
         intervalId = setInterval(tick, LIMIT_CHECK_INTERVAL_MS);
      }

      return () => {
         if (worker) {
            worker.postMessage({ type: 'STOP' });
            worker.terminate();
         }
         if (intervalId) clearInterval(intervalId);
      };
   }, [status, tick]);

   const startRecording = useCallback(async () => {
      if (blockOfflineAction() || busy.current || isActive(activeChatJid)) return;
      busy.current = true;

      try {
         setError(null);
         const rec = new Mp3Recorder();
         const micStream = await rec.start();

         setSession(activeChatJid, {
            recorder: rec,
            stream: micStream,
            startedAt: Date.now(),
            accumulatedMs: 0,
            ampHistory: [],
            status: 'recording',
         });

         setStream(micStream);
         setStatus('recording');
         setCanSend(false);
         setLimitReached(false);
         canSendRef.current = false;
         limitRef.current = false;

         window.dispatchEvent(new CustomEvent('AUDIO_INTERRUPT_EVENT'));
      } catch (err) {
         deleteSession(activeChatJid);
         setStatus('idle');
         setError(MIC_ERRORS[err.name] ?? 'Failed to start recording');
      } finally {
         busy.current = false;
      }
   }, [activeChatJid]);

   const stopRecording = useCallback(async () => {
      if (blockOfflineAction()) return;
      const session = getSession(activeChatJid);
      if (busy.current || !session?.recorder) return;
      busy.current = true;

      try {
         const [buffer, blob] = await session.recorder.stop().getMp3();
         const file = new File(buffer, 'voice.mp3', { type: blob.type, lastModified: Date.now() });
         file.metadata = { audioType: 'recording' };

         deleteSession(activeChatJid);
         setStatus('stopped');
         setStream(null);
         onStop?.(file, blob);
      } catch {
         setError('Failed to process recording');
         deleteSession(activeChatJid);
         setStatus('idle');
      } finally {
         busy.current = false;
      }
   }, [activeChatJid, onStop]);

   const pauseRecording = useCallback(() => {
      const session = getSession(activeChatJid);
      if (!session?.recorder || session.recorder.isPaused) return;

      session.accumulatedMs += Date.now() - session.startedAt;
      session.recorder.stopTracks();
      session.recorder.pause();
      updateSession(activeChatJid, { status: 'paused', stream: null });
      setStream(null);
      setStatus('paused');
   }, [activeChatJid]);

   const resumeRecording = useCallback(async () => {
      const session = getSession(activeChatJid);
      if (!session?.recorder?.isPaused) return;

      try {
         const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
         session.recorder.swapStream(newStream);
         session.recorder.resume();
         updateSession(activeChatJid, {
            status: 'recording',
            stream: newStream,
            startedAt: Date.now(),
         });
         setStream(newStream);
         setStatus('recording');
      } catch {
         setError('Failed to resume microphone');
      }
   }, [activeChatJid]);

   const cancelRecording = useCallback(() => {
      if (busy.current) return;
      const session = getSession(activeChatJid);
      session?.recorder?.stop();
      deleteSession(activeChatJid);
      setStatus('idle');
      setStream(null);
      setCanSend(false);
      setLimitReached(false);
   }, [activeChatJid]);

   useEffect(() => {
      if (isCallActive && status === 'recording') {
         pauseRecording();
      }
   }, [isCallActive, status, pauseRecording]);

   return {
      error,
      stream,
      canSend,
      limitReached,
      tick,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      cancelRecording,
      isRecording: status === 'recording',
      isPaused: status === 'paused',
   };
}
